import type { TFunction } from 'i18next';

import { ActionStatus } from '../types/actionTypes';

/** Azure DevOps–style in-progress blue (not brand red). */
export const ACTION_RUNNING_STATUS_BLUE = '#0078D4';

export const ACTION_STATUS_COLORS: Record<string, string> = {
  INITIALIZED: '#6c757d',
  IN_PROGRESS: ACTION_RUNNING_STATUS_BLUE,
  COMPLETED: '#4CAF50',
  FAILED: '#f44336',
  CANCELLED: '#ff9800',
  CANCELLING: '#ff9800',
  QUEUED: '#6c757d',
  RUNNING: ACTION_RUNNING_STATUS_BLUE,
  SUCCEEDED: '#4CAF50',
  PARTIAL: '#ff9800',
  PARTIAL_SUCCESS: '#ff9800',
  MIXED: '#607d8b',
  TIMED_OUT: '#f44336',
  BLOCKED: '#795548',
  SKIPPED: '#6c757d',
  OUTCOME_UNCONFIRMED: '#607d8b',
  pending: '#6c757d',
  started: ACTION_RUNNING_STATUS_BLUE,
  success: '#4CAF50',
  failure: '#f44336',
  canceled: '#ff9800',
  busy: '#ff9800',
};

export const ACTION_STATUS_LABELS: Record<string, string> = {
  INITIALIZED: 'Queued',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  CANCELLING: 'Cancelling',
  QUEUED: 'Queued',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  PARTIAL: 'Mixed outcomes',
  PARTIAL_SUCCESS: 'Partial success',
  MIXED: 'Mixed',
  TIMED_OUT: 'Timed out',
  BLOCKED: 'Blocked',
  SKIPPED: 'Skipped',
  OUTCOME_UNCONFIRMED: 'Outcome unconfirmed',
  pending: 'Pending',
  started: 'Started',
  success: 'Completed',
  failure: 'Failed',
  canceled: 'Cancelled',
  busy: 'Busy',
};

const ACTION_STATUS_LABEL_KEYS: Record<string, string> = {
  INITIALIZED: 'actionsWorkbench.status.queued',
  IN_PROGRESS: 'actionsWorkbench.status.inProgress',
  COMPLETED: 'actionsWorkbench.status.completed',
  FAILED: 'actionsWorkbench.status.failed',
  CANCELLED: 'actionsWorkbench.status.cancelled',
  CANCELLING: 'actionsWorkbench.status.cancelling',
  QUEUED: 'actionsWorkbench.status.queued',
  RUNNING: 'actionsWorkbench.status.running',
  SUCCEEDED: 'actionsWorkbench.status.succeeded',
  PARTIAL: 'actionsWorkbench.status.mixedOutcomes',
  PARTIAL_SUCCESS: 'actionsWorkbench.status.partialSuccess',
  MIXED: 'actionsWorkbench.status.mixed',
  TIMED_OUT: 'actionsWorkbench.status.timedOut',
  BLOCKED: 'actionsWorkbench.status.blocked',
  SKIPPED: 'actionsWorkbench.status.skipped',
  OUTCOME_UNCONFIRMED: 'actionsWorkbench.status.outcomeUnconfirmed',
  pending: 'actionsWorkbench.status.pending',
  started: 'actionsWorkbench.status.started',
  success: 'actionsWorkbench.status.completed',
  failure: 'actionsWorkbench.status.failed',
  canceled: 'actionsWorkbench.status.cancelled',
  busy: 'actionsWorkbench.status.busy',
};

export const normalizeActionStatus = (status: ActionStatus | null | undefined): string => {
  return String(status ?? 'INITIALIZED').trim();
};

export const resolveActionDisplayStatus = (
  action: {
    status?: ActionStatus | null;
    aggregateStatus?: ActionStatus | null;
    actionDetails?: Array<{ status?: ActionStatus | null }>;
  } | null | undefined
): ActionStatus => {
  if (action?.aggregateStatus) {
    return action.aggregateStatus;
  }

  const parentStatus = action?.status ?? 'INITIALIZED';
  if (isTerminalActionStatus(parentStatus)) {
    return parentStatus;
  }

  const targetStatuses = (action?.actionDetails ?? [])
    .map((detail) => normalizeActionStatus(detail.status).toUpperCase());
  if (targetStatuses.length === 0 || targetStatuses.some((status) => !isTerminalActionStatus(status))) {
    return parentStatus;
  }

  const succeeded = targetStatuses.filter((status) =>
    status === 'COMPLETED' || status === 'SUCCESS' || status === 'SUCCEEDED'
  ).length;
  if (succeeded === targetStatuses.length) {
    return 'COMPLETED';
  }
  if (succeeded > 0) {
    return 'PARTIAL_SUCCESS';
  }
  if (targetStatuses.every((status) => status === 'CANCELLED' || status === 'CANCELED')) {
    return 'CANCELLED';
  }
  if (targetStatuses.every((status) => status === 'BLOCKED')) {
    return 'BLOCKED';
  }
  if (targetStatuses.every((status) => status === 'FAILED' || status === 'FAILURE' || status === 'TIMED_OUT')) {
    return 'FAILED';
  }
  if (targetStatuses.every((status) => status === 'OUTCOME_UNCONFIRMED')) {
    return 'OUTCOME_UNCONFIRMED';
  }
  return 'MIXED';
};

export const isTerminalActionStatus = (status: ActionStatus | null | undefined): boolean => {
  const normalized = normalizeActionStatus(status).toUpperCase();
  return normalized === 'COMPLETED'
    || normalized === 'SUCCEEDED'
    || normalized === 'FAILED'
    || normalized === 'CANCELLED'
    || normalized === 'TIMED_OUT'
    || normalized === 'BLOCKED'
    || normalized === 'MIXED'
    || normalized === 'PARTIAL_SUCCESS'
    || normalized === 'PARTIAL'
    || normalized === 'OUTCOME_UNCONFIRMED';
};

export const getActionStatusColor = (status: ActionStatus | null | undefined): string => {
  const normalized = normalizeActionStatus(status);
  return ACTION_STATUS_COLORS[normalized] ?? ACTION_STATUS_COLORS[normalized.toUpperCase()] ?? '#6c757d';
};

export const getActionStatusLabel = (
  status: ActionStatus | null | undefined,
  t?: TFunction
): string => {
  const normalized = normalizeActionStatus(status);
  const statusKey = ACTION_STATUS_LABEL_KEYS[normalized] ?? ACTION_STATUS_LABEL_KEYS[normalized.toUpperCase()];
  const defaultValue = ACTION_STATUS_LABELS[normalized] ?? ACTION_STATUS_LABELS[normalized.toUpperCase()] ?? 'Unknown';
  if (!statusKey) {
    return String(t ? t('actionsWorkbench.status.unknown', { defaultValue }) : defaultValue);
  }
  return String(t ? t(statusKey, { defaultValue }) : defaultValue);
};
